import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getTrainingCamps, createTrainingCamp, updateTrainingCamp, deleteTrainingCamp, getFighters, createFight } from '../services/api';
import { trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { format, parseISO, isWithinInterval, isFuture, isPast } from 'date-fns';
import { 
  Plus, 
  Calendar as CalendarIcon,
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Swords,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

// Training Camps List Page
export const TrainingCampsListPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterFighterId = searchParams.get('fighter');
  
  const [camps, setCamps] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSport, setFilterSport] = useState('all'); // 'all', 'mma', 'bjj'

  useEffect(() => {
    loadData();
    trackFeatureUsage('training_camps', 'view').catch(() => {});
  }, [filterFighterId]);

  const loadData = async () => {
    try {
      const [campsRes, fightersRes] = await Promise.all([
        getTrainingCamps(filterFighterId),
        getFighters()
      ]);
      setCamps(campsRes.data);
      setFighters(fightersRes.data);
    } catch (error) {
      toast.error('Failed to load training camps');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const getCampStatus = (camp) => {
    const now = new Date();
    const startDate = parseISO(camp.start_date);
    const endDate = parseISO(camp.end_date);
    
    if (camp.status === 'completed') return 'completed';
    if (isPast(endDate)) return 'completed';
    if (isFuture(startDate)) return 'upcoming';
    if (isWithinInterval(now, { start: startDate, end: endDate })) return 'active';
    return camp.status;
  };

  const filteredCamps = camps.filter(camp => {
    // Filter by status
    if (filterStatus !== 'all' && getCampStatus(camp) !== filterStatus) return false;
    // Filter by sport type (treat missing/null sport_type as 'mma')
    const campSportType = camp.sport_type || 'mma';
    if (filterSport !== 'all' && campSportType !== filterSport) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="training-camps-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">
              Camp Management
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Fight Camps
            </h1>
            <p className="text-zinc-400 mt-1">{camps.length} camp{camps.length !== 1 ? 's' : ''} scheduled</p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/training-camps/new')}
              className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading font-semibold tracking-wide"
              data-testid="add-camp-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Start Fight Camp
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 h-12 bg-zinc-900 border-zinc-800" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Camps</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilterSport('all')}
              className={`px-4 py-2 rounded-sm border transition-all ${
                filterSport === 'all'
                  ? 'border-red-600 bg-red-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              All Sports
            </button>
            <button
              onClick={() => setFilterSport('mma')}
              className={`px-4 py-2 rounded-sm border transition-all flex items-center gap-2 ${
                filterSport === 'mma'
                  ? 'border-red-600 bg-red-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              <Swords className="w-4 h-4" />
              MMA
            </button>
            <button
              onClick={() => setFilterSport('bjj')}
              className={`px-4 py-2 rounded-sm border transition-all flex items-center gap-2 ${
                filterSport === 'bjj'
                  ? 'border-blue-600 bg-blue-600/10 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              <Dumbbell className="w-4 h-4" />
              BJJ
            </button>
          </div>
        </div>

        {/* Camps List */}
        {filteredCamps.length > 0 ? (
          <div className="space-y-4">
            {filteredCamps.map((camp, index) => {
              const status = getCampStatus(camp);
              return (
                <motion.div
                  key={camp.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                  data-testid={`camp-card-${camp.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-sm flex items-center justify-center",
                        status === 'active' && "bg-green-600/20",
                        status === 'upcoming' && "bg-blue-600/20",
                        status === 'completed' && "bg-zinc-700/20"
                      )}>
                        {status === 'active' && <Clock className="w-6 h-6 text-green-500" />}
                        {status === 'upcoming' && <CalendarIcon className="w-6 h-6 text-blue-500" />}
                        {status === 'completed' && <CheckCircle className="w-6 h-6 text-zinc-500" />}
                      </div>
                      <div>
                        <h3 className="text-lg text-white font-medium">{camp.name}</h3>
                        <Link 
                          to={`/fighters/${camp.fighter_id}`}
                          className="text-zinc-400 hover:text-red-500 text-sm flex items-center gap-1"
                        >
                          <Users className="w-4 h-4" />
                          {getFighterName(camp.fighter_id)}
                        </Link>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-zinc-300 text-sm">
                          {format(parseISO(camp.start_date), 'M/d')} - {format(parseISO(camp.end_date), 'M/d/yyyy')}
                        </p>
                        <span className={cn(
                          "text-xs uppercase font-medium",
                          status === 'active' && "text-green-500",
                          status === 'upcoming' && "text-blue-500",
                          status === 'completed' && "text-zinc-500"
                        )}>
                          {status}
                        </span>
                      </div>
                      
                      {isAdmin && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/training-camps/${camp.id}/edit`)}
                            className="text-zinc-400 hover:text-white"
                            data-testid={`edit-camp-${camp.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {camp.description && (
                    <p className="text-zinc-500 text-sm mt-4 pl-16">{camp.description}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-16 px-8 text-center bg-zinc-900/30 border border-zinc-800 rounded-sm"
          >
            <div className="w-16 h-16 bg-red-600/20 border border-red-600/30 rounded-sm flex items-center justify-center mb-6">
              <CalendarIcon className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="font-heading text-2xl text-white uppercase tracking-tight mb-2">
              No Active Fight Camps
            </h3>
            <p className="text-zinc-400 mb-6 max-w-md">
              Fight camps are the heart of preparation. Create a camp to organize training blocks, 
              track progress, and prepare your fighters for their next bout.
            </p>
            {isAdmin && (
              <Button 
                onClick={() => navigate('/training-camps/new')}
                className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow uppercase font-heading tracking-wider px-6"
                data-testid="start-first-camp-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Start Fight Camp
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Training Camp Form Page
export const TrainingCampFormPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEdit = window.location.pathname.includes('/edit');
  const campId = isEdit ? window.location.pathname.split('/')[2] : null;
  
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showFightSection, setShowFightSection] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    fighter_id: '',
    start_date: '',
    end_date: '',
    description: '',
    sport_type: 'mma'  // 'mma' or 'bjj'
  });
  
  // Fight data for optional linked fight creation
  const [fightData, setFightData] = useState({
    event_name: '',
    opponent_name: '',
    fight_date: '',
    location: '',
    weight_class: ''
  });

  const WEIGHT_CLASSES = [
    'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight', 
    'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight', 'Heavyweight'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const fightersRes = await getFighters();
      setFighters(fightersRes.data);
      
      if (isEdit && campId) {
        const campsRes = await getTrainingCamps();
        const camp = campsRes.data.find(c => c.id === campId);
        if (camp) {
          setFormData({
            name: camp.name,
            fighter_id: camp.fighter_id,
            start_date: camp.start_date,
            end_date: camp.end_date,
            description: camp.description || '',
            sport_type: camp.sport_type || 'mma'
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let createdCamp;
      if (isEdit) {
        await updateTrainingCamp(campId, formData);
        toast.success('Training camp updated');
      } else {
        const campRes = await createTrainingCamp(formData);
        createdCamp = campRes.data;
        toast.success('Training camp created');
        
        // If fight section is filled out, create the fight too
        if (showFightSection && fightData.event_name && fightData.fight_date) {
          try {
            const selectedFighter = fighters.find(f => f.id === formData.fighter_id);
            // Convert MM/DD/YYYY to YYYY-MM-DD for API
            let apiDate = fightData.fight_date;
            if (fightData.fight_date.includes('/')) {
              const [month, day, year] = fightData.fight_date.split('/');
              apiDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            await createFight({
              fighter_id: formData.fighter_id,
              event_name: fightData.event_name,
              opponent_name: fightData.opponent_name || null,
              fight_date: apiDate,
              location: fightData.location || null,
              weight_class: fightData.weight_class || selectedFighter?.weight_class || 'Lightweight',
              sport_type: formData.sport_type
            });
            toast.success('Fight/Competition also scheduled!');
          } catch (fightError) {
            toast.error('Camp created but failed to schedule fight');
          }
        }
      }
      navigate('/training-camps');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save training camp'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTrainingCamp(campId);
      toast.success('Training camp deleted');
      navigate('/training-camps');
    } catch (error) {
      toast.error('Failed to delete training camp');
    }
  };

  if (!isAdmin) {
    navigate('/training-camps');
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="camp-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/training-camps')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Training Camps
        </Button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">
            {isEdit ? 'Edit Training Camp' : 'New Training Camp'}
          </h1>
          {isEdit && (
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="border-red-800 text-red-500 hover:bg-red-950"
              data-testid="delete-camp-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sport Type Toggle */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Camp Type *</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, sport_type: 'mma' })}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  formData.sport_type === 'mma'
                    ? 'border-red-600 bg-red-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="camp-type-mma"
              >
                <Swords className="w-5 h-5" />
                MMA Camp
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, sport_type: 'bjj' })}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  formData.sport_type === 'bjj'
                    ? 'border-blue-600 bg-blue-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="camp-type-bjj"
              >
                <Dumbbell className="w-5 h-5" />
                BJJ Camp
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">Camp Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="UFC 300 Prep Camp"
              className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
              required
              data-testid="camp-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fighter" className="text-zinc-300">Fighter *</Label>
            <Select 
              value={formData.fighter_id} 
              onValueChange={(value) => setFormData({ ...formData, fighter_id: value })}
            >
              <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="camp-fighter-select">
                <SelectValue placeholder="Select fighter" />
              </SelectTrigger>
              <SelectContent>
                {fighters.map(fighter => (
                  <SelectItem key={fighter.id} value={fighter.id}>
                    {fighter.name} - {fighter.weight_class}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-300">Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal bg-zinc-900 border-zinc-800",
                      !formData.start_date && "text-zinc-500"
                    )}
                    data-testid="camp-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.start_date ? format(parseISO(formData.start_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                  <Calendar
                    mode="single"
                    selected={formData.start_date ? parseISO(formData.start_date) : undefined}
                    onSelect={(date) => setFormData({ ...formData, start_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">End Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal bg-zinc-900 border-zinc-800",
                      !formData.end_date && "text-zinc-500"
                    )}
                    data-testid="camp-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.end_date ? format(parseISO(formData.end_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                  <Calendar
                    mode="single"
                    selected={formData.end_date ? parseISO(formData.end_date) : undefined}
                    onSelect={(date) => setFormData({ ...formData, end_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-zinc-300">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Camp details, focus areas, etc."
              className="min-h-[120px] bg-zinc-900 border-zinc-800 focus:border-red-600"
              data-testid="camp-description-input"
            />
          </div>

          {/* Optional: Schedule Fight/Competition */}
          {!isEdit && (
            <div className="border-t border-zinc-800 pt-6">
              <button
                type="button"
                onClick={() => setShowFightSection(!showFightSection)}
                className="w-full flex items-center justify-between p-4 bg-zinc-800/50 rounded-sm border border-zinc-700 hover:border-zinc-600 transition-colors"
                data-testid="toggle-fight-section"
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center",
                    showFightSection ? "bg-red-600/20" : "bg-zinc-700/50"
                  )}>
                    <Target className={cn("w-5 h-5", showFightSection ? "text-red-500" : "text-zinc-400")} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium">
                      {formData.sport_type === 'bjj' ? 'Schedule Competition' : 'Schedule Fight'}
                    </p>
                    <p className="text-zinc-500 text-sm">
                      Link this camp to an upcoming {formData.sport_type === 'bjj' ? 'tournament' : 'bout'}
                    </p>
                  </div>
                </div>
                {showFightSection ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </button>
              
              {showFightSection && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 bg-zinc-800/30 rounded-sm border border-zinc-700 space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">
                        {formData.sport_type === 'bjj' ? 'Tournament Name *' : 'Event Name *'}
                      </Label>
                      <Input
                        value={fightData.event_name}
                        onChange={(e) => setFightData({ ...fightData, event_name: e.target.value })}
                        placeholder={formData.sport_type === 'bjj' ? 'IBJJF World Championship' : 'UFC 300'}
                        className="h-10 bg-zinc-900 border-zinc-700"
                        data-testid="fight-event-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">
                        {formData.sport_type === 'bjj' ? 'Event Date *' : 'Fight Date *'}
                      </Label>
                      <Input
                        type="text"
                        placeholder="MM/DD/YYYY"
                        value={fightData.fight_date}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9/]/g, '');
                          if (value.length === 2 && !value.includes('/')) value = value + '/';
                          else if (value.length === 5 && value.split('/').length === 2) value = value + '/';
                          if (value.length <= 10) setFightData({ ...fightData, fight_date: value });
                        }}
                        className="h-10 bg-zinc-900 border-zinc-700"
                        data-testid="fight-date"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Opponent (Optional)</Label>
                      <Input
                        value={fightData.opponent_name}
                        onChange={(e) => setFightData({ ...fightData, opponent_name: e.target.value })}
                        placeholder="TBA"
                        className="h-10 bg-zinc-900 border-zinc-700"
                        data-testid="fight-opponent"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Location (Optional)</Label>
                      <Input
                        value={fightData.location}
                        onChange={(e) => setFightData({ ...fightData, location: e.target.value })}
                        placeholder="Las Vegas, NV"
                        className="h-10 bg-zinc-900 border-zinc-700"
                        data-testid="fight-location"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Weight Class</Label>
                    <Select 
                      value={fightData.weight_class} 
                      onValueChange={(value) => setFightData({ ...fightData, weight_class: value })}
                    >
                      <SelectTrigger className="h-10 bg-zinc-900 border-zinc-700" data-testid="fight-weight-class">
                        <SelectValue placeholder="Select weight class" />
                      </SelectTrigger>
                      <SelectContent>
                        {WEIGHT_CLASSES.map(wc => (
                          <SelectItem key={wc} value={wc}>{wc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <p className="text-xs text-zinc-500">
                    This will create both the training camp and the {formData.sport_type === 'bjj' ? 'competition' : 'fight'} in one step.
                  </p>
                </motion.div>
              )}
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/training-camps')}
              className="flex-1 h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.name || !formData.fighter_id || !formData.start_date || !formData.end_date}
              className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
              data-testid="save-camp-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update Camp' : 'Create Camp'}
            </Button>
          </div>
        </form>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Training Camp</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this training camp? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
